import { doc, getDoc, runTransaction } from 'firebase/firestore';
import { firestore } from '@api/Firebase/Firebase';
import { countersRef } from '@api/Orders/ShortId';
import { buildCatalogSnapshot, writeCatalogSnapshot } from '@api/Catalog/CatalogSnapshot';

// ============================================================================
// Versión del catálogo — la llave de las cachés (auditoría 2026-09-06).
//
// Un solo doc, `counters/catalog-version`, que el ADMIN incrementa cada vez que
// guarda cualquier pieza del catálogo (producto, receta, modificador, insumo,
// categoría). Quien necesita el catálogo (POS, web, server) lee ese doc —
// 1 lectura — y solo recarga las colecciones (~340 docs) si la versión cambió.
// Si el doc no existe todavía, un TTL corto de respaldo evita quedarse con
// catálogo viejo.
//
// ⚠️ PARIDAD ×3: mismo id de doc, mismo campo y misma regla de frescura en
// krispis-server (`api/Catalog/CatalogCache.ts`) y krispis-web
// (`api/Catalog/CatalogVersion.ts`). Cambiar aquí = cambiar allá.
// ============================================================================

export const CATALOG_VERSION_DOC_ID = 'catalog-version';
export const CATALOG_VERSION_FIELD = 'version';

/** Sin doc de versión, cuánto se confía en lo cacheado antes de releer. */
export const CATALOG_FALLBACK_TTL_MS = 5 * 60_000;

export interface CatalogCacheEntry<T> {
    value: T;
    /** Versión con la que se cargó; `null` si el doc de versión no existía. */
    version: number | null;
    loadedAt: number;
}

/** Versión numérica a partir de los datos del doc (o `null` si no hay doc / no es número). */
export const readCatalogVersionFromData = (data: unknown): number | null => {
    if (!data || typeof data !== 'object') return null;
    const raw = (data as Record<string, unknown>)[CATALOG_VERSION_FIELD];
    const version = Number(raw);
    return Number.isFinite(version) ? version : null;
};

/**
 * ¿Se puede reutilizar lo cacheado?
 * - Con versión conocida: sí, exactamente cuando la versión no cambió.
 * - Sin versión (doc ausente): sí, mientras no venza el TTL de respaldo.
 */
export const isCatalogCacheFresh = <T>(
    entry: CatalogCacheEntry<T> | undefined,
    currentVersion: number | null,
    now: number,
    fallbackTtlMs: number = CATALOG_FALLBACK_TTL_MS
): boolean => {
    if (!entry) return false;
    if (currentVersion !== null) return entry.version === currentVersion;
    return now - entry.loadedAt < fallbackTtlMs;
};

export const catalogVersionRef = doc(countersRef, CATALOG_VERSION_DOC_ID);

/** La versión vigente (1 lectura), o `null` si el doc todavía no existe. */
export const readCatalogVersion = async (): Promise<number | null> => {
    const snapshot = await getDoc(catalogVersionRef);
    return snapshot.exists() ? readCatalogVersionFromData(snapshot.data()) : null;
};

/**
 * Publica el catálogo tras guardar/borrar cualquier pieza: escribe el snapshot
 * pre-armado (`catalog/snapshot`) y sube la versión.
 *
 * ## Orden, que NO es negociable: snapshot y DESPUÉS versión
 * Al revés, un fallo entre las dos dejaría a POS, web y server viendo versión
 * nueva con catálogo viejo, creyéndose al día. En este orden un fallo deja
 * versión vieja con snapshot nuevo: todos siguen con su caché, que es el estado
 * seguro, y el próximo guardado lo corrige.
 *
 * ## Sí lanza, a diferencia del `publishCatalog` que reemplazó
 * Hasta el 2026-09-09 esta función **se tragaba los errores**, y con el snapshot
 * eso pasó de molesto a grave: un fallo silencioso congelaría el catálogo de
 * toda la operación sin que nadie se enterara (antes había un TTL de respaldo
 * que lo tapaba; ya no). Ahora **avisa a quien guardó** para que reintente. El
 * guardado de la pieza ya ocurrió: lo que falta es republicar, y por eso el
 * mensaje lo dice así.
 *
 * Cuesta 336 lecturas por publicación. Se paga solo cuando alguien GUARDA algo
 * del catálogo —unas pocas veces al día— para que las decenas de miles de
 * lecturas de la operación cuesten 1 cada una.
 */
export const publishCatalog = async (reason: string): Promise<void> => {
    const version = await reserveCatalogVersion(reason);
    try {
        const snapshot = await buildCatalogSnapshot(version, reason);
        await writeCatalogSnapshot(snapshot);
    } catch (error) {
        console.error('No se pudo publicar el catálogo', error);
        throw new Error(
            `Se guardó el cambio, pero el catálogo NO se pudo republicar, así que el POS y la web siguen con la versión anterior. Vuelve a guardar. (${
                error instanceof Error ? error.message : String(error)
            })`
        );
    }
};

/**
 * Reserva la SIGUIENTE versión de forma atómica y la deja escrita en
 * `counters/catalog-version`.
 *
 * ⚠️ **Con transacción, y es la excepción que el CLAUDE.md raíz ya contempla**
 * (los contadores de `counters` — el folio hace exactamente esto). La primera
 * versión (2026-09-09) hacía leer-sumar-escribir en cliente, y eso perdía
 * cambios: dos personas guardando con segundos de diferencia —y armar el
 * snapshot tarda lo que tardan 336 lecturas— leían la misma versión, calculaban
 * la misma siguiente, y el último `setDoc` ganaba. El snapshot publicado se
 * quedaba sin el cambio del primero, con un número que POS, web y server ya
 * habían visto, así que **nadie volvía a leer** y el cambio desaparecía sin
 * rastro.
 *
 * Reservar ANTES de publicar también arregla el otro extremo: si el snapshot
 * falla, el número queda quemado y nunca se reutiliza, así que no pueden existir
 * dos snapshots distintos con la misma versión.
 */
export const reserveCatalogVersion = async (reason: string): Promise<number> => {
    try {
        return await runTransaction(firestore, async (transaction) => {
            const snapshot = await transaction.get(catalogVersionRef);
            const actual = snapshot.exists() ? readCatalogVersionFromData(snapshot.data()) || 0 : 0;
            const siguiente = actual + 1;
            transaction.set(
                catalogVersionRef,
                {
                    [CATALOG_VERSION_FIELD]: siguiente,
                    scope: 'catalog',
                    lastReason: reason,
                    lastUpdate: new Date()
                },
                { merge: true }
            );
            return siguiente;
        });
    } catch (error) {
        // Sin versión no se puede publicar: dos snapshots con el mismo número
        // dejarían a los consumidores creyéndose al día con catálogo viejo.
        console.error('No se pudo reservar la versión del catálogo', error);
        throw new Error(
            'Se guardó el cambio, pero no se pudo reservar la versión del catálogo, así que NO se republicó. Vuelve a guardar.'
        );
    }
};
